import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import {
  PulseBackgroundWave,
  type PulseBackgroundWaveProps,
} from "./pulse-background-wave";

export type PulseBackgroundSpawnOptions = Omit<
  PulseBackgroundWaveProps,
  "onComplete"
> & {
  onComplete?: () => void;
};

interface PulseBackgroundContextValue {
  spawn: (options: PulseBackgroundSpawnOptions) => string;
  dismiss: (id: string) => void;
}

const PulseBackgroundContext = createContext<PulseBackgroundContextValue | null>(
  null,
);

interface SpawnEntry {
  id: string;
  options: PulseBackgroundSpawnOptions;
}

export function PulseBackgroundProvider({ children }: { children: ReactNode }) {
  const [spawns, setSpawns] = useState<SpawnEntry[]>([]);

  const dismiss = useCallback((id: string) => {
    setSpawns((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const spawn = useCallback((options: PulseBackgroundSpawnOptions) => {
    const id = crypto.randomUUID();
    setSpawns((current) => [...current, { id, options }]);
    return id;
  }, []);

  return (
    <PulseBackgroundContext.Provider value={{ spawn, dismiss }}>
      {children}
      {spawns.map(({ id, options }) => (
        <PulseBackgroundWave
          key={id}
          {...options}
          onComplete={() => {
            options.onComplete?.();
            if (options.removeOnComplete) {
              dismiss(id);
            }
          }}
        />
      ))}
    </PulseBackgroundContext.Provider>
  );
}

export function usePulseBackground() {
  const context = useContext(PulseBackgroundContext);
  if (!context) {
    throw new Error(
      "usePulseBackground must be used within PulseBackgroundProvider",
    );
  }
  return context;
}
